import { ReactNode } from 'react';
import gettext from '@sdk/lang/gettext';
import Tooltip from '@sdk/common/tooltip/Tooltip';

import { PathnameEnum } from '@web/Router/router.types';
import { generatePath } from '@web/Router/router.utils';
import PlusIcon from '@web/assets/svg/UI/plus.svg';
import PinIcon from '@web/assets/svg/UI/pin.svg';
import { ConversationBasic } from '@web/conversations/types/Conversation';
import { useScrollbarGutterFallback } from '@web/common/hooks/useScrollbarGutterFallback';

import { useListHeightObserver, useTogglePinConversation } from './group.hooks';
import {
  CounterBadge,
  Header,
  Item,
  ItemLink,
  List,
  PinButton,
  PlusButton,
  Wrapper,
} from './group.styles';

type Props = {
  createPath?: PathnameEnum;
  isAccessRestricted?: boolean;
  items: ConversationBasic[];
  title: ReactNode;
  tooltipContent?: string;
};

export const Group = ({ createPath, tooltipContent, items, isAccessRestricted, title }: Props) => {
  const { setRef, shouldApplyFallback } = useScrollbarGutterFallback();
  const setListref = useListHeightObserver();
  const togglePin = useTogglePinConversation();

  return (
    <Wrapper isActive={!isAccessRestricted}>
      <Header>
        {title}
        {createPath && (
          <PlusButton
            aria-label={gettext('Create')}
            title={gettext('Create')}
            to={generatePath(createPath)}>
            <PlusIcon aria-hidden={true} />
          </PlusButton>
        )}
      </Header>
      <List
        ref={(node) => {
          setListref(node);
          setRef(node);
        }}
        shouldApplyFallback={shouldApplyFallback}>
        {items.map((conversation) => (
          <Item
            data-for="conversation-group-tooltip"
            data-tip={tooltipContent}
            data-place="right"
            key={conversation.id}
            isPinned={conversation.isPinned}
            isSilenced={conversation.isSilenced}>
            <ItemLink
              to={generatePath(PathnameEnum.CONVERSATION_DETAILS, {
                params: { id: conversation.id, slug: conversation.slug },
              })}>
              {conversation.name}
            </ItemLink>
            <PinButton
              aria-label={conversation.isPinned ? gettext('Unpin') : gettext('Pin')}
              title={conversation.isPinned ? gettext('Unpin') : gettext('Pin')}
              onClick={() => togglePin(conversation)}
              isPinned={conversation.isPinned}>
              <PinIcon aria-hidden={true} />
            </PinButton>
            {conversation.unreadMessagesCount > 0 && (
              <CounterBadge>
                {conversation.unreadMessagesCount > 99 ? '99+' : conversation.unreadMessagesCount}
              </CounterBadge>
            )}
          </Item>
        ))}

        <Tooltip maxWidth={211} attachToBody id="conversation-group-tooltip" />
      </List>
    </Wrapper>
  );
};
